import type { Team } from "../../../common/types.ts";
import { levelToAmount } from "../../../common/budgetLevels.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, local } from "../../util/index.ts";
import getPayroll from "./getPayroll.ts";

const keys = ["scouting", "coaching", "health", "facilities"] as const;
type Levels = Pick<Team["budget"], (typeof keys)[number]>;
type Plan = {
	season: number;
	version: number;
	payroll: number;
	revenue: number;
	recoveryStart: number;
};
type FinancialTeam = Team & { aiFinancialPlan?: Plan };
const bound = (n: number, low: number, high: number) =>
	Math.max(low, Math.min(high, n));

// Plans made with older rules are recalculated (3: revenue share and yearly growth cap, zero-revenue seasons ignored)
export const PLAN_VERSION = 3;
// Payroll aims at about half of revenue (plus the cash reserve), like big league clubs, and moves toward it gradually
const REVENUE_SHARE = 0.5;
const MAX_YEARLY_GROWTH = 1.25;

// Pure annual planning: all amounts are in the game's thousands of dollars.
export const planAiFinances = (input: {
	revenue: number;
	cash: number;
	payroll: number;
	reference: number;
	minPayroll: number;
	salaryCap: number;
	capType: string;
	luxuryPayroll: number;
	luxuryTax: number;
	budgetEnabled: boolean;
	rebuilding: boolean;
	levels: Levels;
	recoveryYears: number;
	// Last season's budget, so it rises at most MAX_YEARLY_GROWTH a year
	previous?: number;
}) => {
	const x = input;
	const reference = Math.max(1, x.reference);
	const revenue = Math.max(0, x.revenue);
	// Positive cash can support additional roster investment, but only up to 30% of
	// the league's reference payroll each year. More cash does not raise this cap.
	const reserve = Math.min(Math.max(0, x.cash) * 0.1, reference * 0.3);
	// Old debt stays on the books. Its planning burden ramps in over three years
	// and is bounded, so a century-old negative balance cannot freeze the club.
	const recovery =
		Math.min(Math.max(0, -x.cash) * 0.02, revenue * 0.08) *
		Math.min(1, (x.recoveryYears + 1) / 3);
	const resources = (revenue + reserve - recovery) / reference;
	const base = bound(
		34 + (resources - 0.65) * 45 + (x.cash > reference ? 8 : 0),
		30,
		78,
	);
	const levels = { ...x.levels };
	for (const key of keys) {
		const priority = x.rebuilding
			? key === "coaching"
				? 12
				: key === "scouting"
					? 8
					: 0
			: key === "health"
				? 10
				: key === "coaching"
					? 6
					: 2;
		const target = bound(Math.round(base + priority), 30, 90);
		levels[key] = Math.round(
			bound(
				target,
				Math.max(1, x.levels[key] - 12),
				Math.min(100, x.levels[key] + 20),
			),
		);
	}
	const operations = x.budgetEnabled
		? keys.reduce(
				(sum, key) => sum + levelToAmount(levels[key], x.salaryCap),
				0,
			)
		: 0;
	let affordable = Math.max(
		0,
		revenue * 0.92 + reserve - recovery - operations,
	);
	if (x.capType !== "hard" && affordable > x.luxuryPayroll) {
		affordable =
			x.luxuryPayroll +
			(affordable - x.luxuryPayroll) / (1 + Math.max(0, x.luxuryTax));
	}
	// Gradual adjustment: no forced releases of existing contracts. Retain room
	// to operate while old salaries expire; new commitments still face a budget.
	const floor = Math.max(x.minPayroll, x.payroll * 0.9);
	const ceiling = Math.min(
		reference * 1.2,
		revenue * REVENUE_SHARE + reserve,
		x.previous !== undefined
			? Math.max(x.previous, x.payroll) * MAX_YEARLY_GROWTH
			: Infinity,
	);
	const target = Math.max(floor, Math.min(ceiling, affordable));
	const payroll = x.capType === "none" ? target : Math.min(x.salaryCap, target);
	return { levels, payroll: Math.round(payroll) };
};

export const updateAiFinances = async () => {
	if (!isSport("baseball") || g.get("forceHistoricalRosters")) return;
	const season = g.get("season");
	for (const raw of await idb.cache.teams.getAll()) {
		const t = raw as FinancialTeam;
		if (
			t.disabled ||
			(g.get("userTids").includes(t.tid) &&
				!local.autoPlayUntil &&
				!g.get("spectator"))
		)
			continue;
		if (t.aiFinancialPlan?.season === season && t.aiFinancialPlan.version === PLAN_VERSION)
			continue;
		const rows = await idb.getCopies.teamSeasons(
			{ tid: t.tid, seasons: [season - 3, season] },
			"noCopyCache",
		);
		const current = rows.find((row) => row.season === season);
		const totalRevenue = (row: (typeof rows)[number]) =>
			Object.values(row.revenues).reduce((sum, v) => sum + v, 0);
		// A season with games but no revenue (stats imported with a league file, before it started) is not a season the
		// club earned anything in: averaging it in cut revenue by half or more
		const completed = rows
			.filter(
				(row) =>
					row.season < season &&
					helpers.getTeamSeasonGp(row) > 0 &&
					totalRevenue(row) > 0,
			)
			.slice(-3);
		const payroll = await getPayroll(t.tid);
		let revenue = completed.length
			? completed.reduce((sum, row) => sum + totalRevenue(row), 0) /
				completed.length
			: 0;
		if (revenue <= 0) {
			const gp = current ? helpers.getTeamSeasonGp(current) : 0;
			revenue =
				current && gp >= 20
					? (totalRevenue(current) * g.get("numGames")) / gp
					: Math.max(payroll, g.get("minPayroll")) / 0.7;
		}
		const recoveryStart = t.aiFinancialPlan?.recoveryStart ?? season;
		const result = planAiFinances({
			revenue,
			cash: current?.cash ?? rows.at(-1)?.cash ?? 0,
			payroll,
			reference: Math.max(
				g.get("salaryCap"),
				g.get("minPayroll"),
				g.get("luxuryPayroll"),
			),
			minPayroll: g.get("minPayroll"),
			salaryCap: g.get("salaryCap"),
			capType: g.get("salaryCapType"),
			luxuryPayroll: g.get("luxuryPayroll"),
			luxuryTax: g.get("luxuryTax"),
			budgetEnabled: g.get("budget"),
			rebuilding: t.strategy === "rebuilding",
			levels: t.budget,
			recoveryYears: Math.max(0, season - recoveryStart),
			// Any earlier plan, also one from this season under older rules, so a save doesn't jump all at once
			previous: t.aiFinancialPlan?.payroll,
		});
		if (g.get("budget")) Object.assign(t.budget, result.levels);
		t.aiFinancialPlan = {
			season,
			version: PLAN_VERSION,
			payroll: result.payroll,
			revenue,
			recoveryStart,
		};
		await idb.cache.teams.put(t);
	}
};

export const teamBaseballBudget = async (tid: number) => {
	const t = (await idb.cache.teams.get(tid)) as FinancialTeam | undefined;
	const plan = t?.aiFinancialPlan;
	if (
		plan?.season === g.get("season") &&
		plan.version === PLAN_VERSION &&
		!g.get("userTids").includes(tid)
	) {
		return g.get("salaryCapType") === "none"
			? plan.payroll
			: Math.min(plan.payroll, g.get("salaryCap"));
	}
	return g.get("salaryCapType") === "none"
		? Math.max(g.get("salaryCap"), g.get("minPayroll"), g.get("luxuryPayroll"))
		: g.get("salaryCap");
};

// Expose the exact limit used by signings and call-ups without creating or changing a plan.
export const getAiPayrollBudget = async (tid: number) => {
	if (!isSport("baseball") || g.get("userTids").includes(tid)) return;
	const t = (await idb.cache.teams.get(tid)) as FinancialTeam | undefined;
	if (!t || t.disabled) return;
	const amount = await teamBaseballBudget(tid);
	const payroll = await getPayroll(tid);
	return {
		season: g.get("season"),
		amount,
		payroll,
		room: amount - payroll,
		provisional: t.aiFinancialPlan?.season !== g.get("season") || t.aiFinancialPlan.version !== PLAN_VERSION,
	};
};

